import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Screen } from "@/components/Screen";
import { QuestionCard } from "@/components/QuestionCard";
import { ScaleInput } from "@/components/ScaleInput";
import { ChipGroup } from "@/components/ChipGroup";
import { ResultBadge } from "@/components/ResultBadge";
import { useI18n } from "@/i18n/I18nProvider";
import { calculateCompassResult } from "@/lib/compass";
import { generateDailyRecommendation } from "@/lib/recommendations";
import { todayLocalISO, formatDate } from "@/lib/date";
import { getCheckInByDate, saveCheckIn } from "@/store/store";

const NEED_ORDER = ["action", "rest", "direction", "strategy", "pause"];

const initialAnswers = {
  energy: null,
  mood: null,
  pressure: null,
  clarity: null,
  planRealism: null,
  need: null,
  dailyWin: "",
};

export default function Today() {
  const { t, language } = useI18n();
  const navigate = useNavigate();

  const dateISO = useMemo(() => todayLocalISO(), []);
  const [existing, setExisting] = useState(() => getCheckInByDate(dateISO));
  const [redoing, setRedoing] = useState(false);
  const [answers, setAnswers] = useState(initialAnswers);

  const showForm = !existing || redoing;

  const setField = (field) => (value) =>
    setAnswers((prev) => ({ ...prev, [field]: value }));

  const isValid =
    answers.energy !== null &&
    answers.mood !== null &&
    answers.pressure !== null &&
    answers.clarity !== null &&
    answers.planRealism !== null &&
    answers.need !== null;

  const handleSubmit = () => {
    if (!isValid) return;
    const { result, balanceScore } = calculateCompassResult(answers);
    const resultLabel = t(`result.labels.${result}.title`);
    const recommendation = generateDailyRecommendation(
      result,
      dateISO,
      language,
    );
    const entry = {
      id: dateISO,
      date: dateISO,
      energy: answers.energy,
      mood: answers.mood,
      pressure: answers.pressure,
      clarity: answers.clarity,
      planRealism: answers.planRealism,
      need: answers.need,
      dailyWin: (answers.dailyWin || "").trim(),
      result,
      resultLabel,
      recommendation,
      balanceScore,
      createdAt: new Date().toISOString(),
    };
    // Persist first (spec allows auto-save; the Result screen re-confirms via toast).
    saveCheckIn(entry);
    navigate("/result", { state: { entry, fresh: true } });
  };

  return (
    <Screen testId="today-screen">
      <header className="pt-4">
        <p className="text-xs uppercase tracking-[0.18em] text-[#E8D8B8]/70">
          {t("checkin.title")}
        </p>
        <h1
          className="mt-2 text-3xl sm:text-4xl font-medium leading-tight capitalize"
          style={{ letterSpacing: "-0.02em" }}
          data-testid="today-date"
        >
          {formatDate(dateISO, language)}
        </h1>
      </header>

      {!showForm ? (
        <AlreadyDoneCard
          entry={existing}
          onView={() => navigate("/result")}
          onRedo={() => {
            setRedoing(true);
            setAnswers(initialAnswers);
            setExisting(null);
          }}
        />
      ) : (
        <div className="mt-6 space-y-4" data-testid="today-form">
          <QuestionCard
            number={1}
            title={t("checkin.questions.energy.question")}
            testId="q-energy"
          >
            <ScaleInput
              value={answers.energy}
              onChange={setField("energy")}
              leftLabel={t("checkin.questions.energy.min")}
              rightLabel={t("checkin.questions.energy.max")}
              testIdPrefix="energy"
            />
          </QuestionCard>

          <QuestionCard
            number={2}
            title={t("checkin.questions.mood.question")}
            testId="q-mood"
          >
            <ScaleInput
              value={answers.mood}
              onChange={setField("mood")}
              leftLabel={t("checkin.questions.mood.min")}
              rightLabel={t("checkin.questions.mood.max")}
              testIdPrefix="mood"
            />
          </QuestionCard>

          <QuestionCard
            number={3}
            title={t("checkin.questions.pressure.question")}
            testId="q-pressure"
          >
            <ScaleInput
              value={answers.pressure}
              onChange={setField("pressure")}
              leftLabel={t("checkin.questions.pressure.min")}
              rightLabel={t("checkin.questions.pressure.max")}
              testIdPrefix="pressure"
            />
          </QuestionCard>

          <QuestionCard
            number={4}
            title={t("checkin.questions.clarity.question")}
            testId="q-clarity"
          >
            <ScaleInput
              value={answers.clarity}
              onChange={setField("clarity")}
              leftLabel={t("checkin.questions.clarity.min")}
              rightLabel={t("checkin.questions.clarity.max")}
              testIdPrefix="clarity"
            />
          </QuestionCard>

          <QuestionCard
            number={5}
            title={t("checkin.questions.planRealism.question")}
            testId="q-planRealism"
          >
            <ScaleInput
              value={answers.planRealism}
              onChange={setField("planRealism")}
              leftLabel={t("checkin.questions.planRealism.min")}
              rightLabel={t("checkin.questions.planRealism.max")}
              testIdPrefix="planRealism"
            />
          </QuestionCard>

          <QuestionCard
            number={6}
            title={t("checkin.questions.need.question")}
            testId="q-need"
          >
            <ChipGroup
              value={answers.need}
              onChange={setField("need")}
              options={NEED_ORDER.map((v) => ({
                value: v,
                label: t(`checkin.questions.need.options.${v}`),
              }))}
              testIdPrefix="need"
            />
          </QuestionCard>

          <QuestionCard
            number={7}
            title={t("checkin.questions.dailyWin.question")}
            testId="q-dailyWin"
          >
            <DailyWinField
              value={answers.dailyWin}
              onChange={setField("dailyWin")}
              placeholder={t("checkin.questions.dailyWin.placeholder")}
              counterTemplate={t("checkin.counter")}
            />
          </QuestionCard>

          <div className="pt-4">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isValid}
              data-testid="today-submit"
              className={`group w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-[15px] font-medium transition-all duration-300 ${
                isValid
                  ? "shadow-[0_10px_30px_-15px_rgba(244,201,93,0.6)] hover:shadow-[0_14px_36px_-14px_rgba(244,201,93,0.8)] active:scale-[0.985]"
                  : "opacity-40 cursor-not-allowed"
              }`}
              style={{
                background:
                  "linear-gradient(180deg, #F5D07A 0%, #F4C95D 100%)",
                color: "#0F172A",
                letterSpacing: "-0.005em",
              }}
            >
              {t("checkin.submit")}
              <ArrowRight
                size={18}
                strokeWidth={2}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </button>
          </div>
        </div>
      )}
    </Screen>
  );
}

function AlreadyDoneCard({ entry, onView, onRedo }) {
  const { t } = useI18n();
  return (
    <div className="mt-6" data-testid="today-already-done">
      <div className="dc-card p-6">
        <p className="text-xs uppercase tracking-[0.16em] text-[#E8D8B8]/75">
          {t("checkin.alreadyDone.eyebrow")}
        </p>
        <div className="mt-4 flex items-center gap-4">
          <ResultBadge result={entry.result} size={68} />
          <div>
            <h2
              className="text-xl font-medium text-[#F8FAFC]"
              style={{ letterSpacing: "-0.015em" }}
            >
              {t(`result.labels.${entry.result}.title`)}
            </h2>
            <p className="mt-1 text-xs text-[#94A3B8]">
              {t("result.balanceLabel")}:{" "}
              <span className="text-[#E8D8B8]">{entry.balanceScore}</span>
            </p>
          </div>
        </div>
        <h3
          className="mt-5 text-base font-medium text-[#F8FAFC]"
          style={{ letterSpacing: "-0.01em" }}
        >
          {t("checkin.alreadyDone.title")}
        </h3>
        <p className="mt-2 text-[14px] text-[#CBD5E1] leading-relaxed">
          {t("checkin.alreadyDone.description")}
        </p>
      </div>

      <div className="mt-6 space-y-3">
        <button
          type="button"
          onClick={onView}
          data-testid="today-view-result"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-[15px] font-medium transition-all duration-300 shadow-[0_10px_30px_-15px_rgba(244,201,93,0.6)] hover:shadow-[0_14px_36px_-14px_rgba(244,201,93,0.8)] active:scale-[0.985]"
          style={{
            background: "linear-gradient(180deg, #F5D07A 0%, #F4C95D 100%)",
            color: "#0F172A",
          }}
        >
          {t("checkin.alreadyDone.view")}
        </button>
        <button
          type="button"
          onClick={onRedo}
          data-testid="today-redo"
          className="w-full inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-medium text-white/80 border border-white/12 hover:bg-white/[0.05] hover:text-white transition-colors duration-200"
        >
          {t("checkin.alreadyDone.redo")}
        </button>
      </div>
    </div>
  );
}

function DailyWinField({ value, onChange, placeholder, counterTemplate }) {
  const max = 240;
  const count = value.length;
  const counterText = counterTemplate.replace("{count}", String(count));
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, max))}
        placeholder={placeholder}
        rows={3}
        data-testid="dailyWin-input"
        className="w-full rounded-2xl bg-white/[0.03] border border-white/10 focus:border-[#F4C95D]/60 focus:outline-none text-[14px] text-[#F8FAFC] placeholder:text-[#94A3B8]/70 px-4 py-3 resize-none transition-colors duration-200"
        style={{ letterSpacing: "-0.005em" }}
      />
      <div className="mt-2 flex justify-end text-[11px] text-[#94A3B8]/80">
        {counterText}
      </div>
    </div>
  );
}
